import { Promise$1, FontType, uniq } from './main.js';

class Provider {
    kind;
    static baseUrl = '';
    constructor(kind) {
        this.kind = kind;
    }
    getUrls(fontsData, families) {
        const providerFonts = this.filterByFontKind(fontsData);
        if (!providerFonts || !providerFonts.length) {
            return [];
        }
        if (!families || !families.length) {
            return [];
        }
        const fontsToProcess = this.filterByFamilies(providerFonts, families);
        if (!fontsToProcess || !fontsToProcess.length) {
            return [];
        }
        return [this.getUrl(fontsToProcess)];
    }
    filterByFontKind(fontsData) {
        return fontsData.filter((font) => font.kind === this.kind);
    }
    // eslint-disable-next-line class-methods-use-this
    filterByFamilies(fontsData, families) {
        return fontsData.filter((font) => families.includes(font.family));
    }
}

class FileProvider extends Provider {
    static baseUrl = 'https://www.thehotelsnetwork.com/_/fonts';
    constructor() {
        super(FontType.File);
    }
    // eslint-disable-next-line class-methods-use-this
    getUrl(fontsData) {
        const keys = uniq(fontsData.map((data) => data.key));
        return `${FileProvider.baseUrl}?family=${keys.join(',')}`;
    }
}

class GoogleProvider extends Provider {
    static baseUrl = 'https://www.thehotelsnetwork.com/gfonts/css2';
    static variants = ':wght@400;700';
    constructor() {
        super(FontType.Google);
    }
    // eslint-disable-next-line class-methods-use-this
    getUrl(fontsData) {
        const keys = uniq(fontsData.map((data) => data.key));
        return `${GoogleProvider.baseUrl}?family=${keys.join(`${GoogleProvider.variants}&family=`)}${GoogleProvider.variants}`;
    }
}

class FontsLoaderService {
    container;
    loadingFamiliesByDoc = new WeakMap();
    constructor(container) {
        this.container = container;
    }
    async addFonts(fontsData, document) {
        const families = this.getFamilies(fontsData);
        const documentFamilies = this.documentFamilies(document);
        if (!this.loadingFamiliesByDoc.has(document)) {
            this.loadingFamiliesByDoc.set(document, []);
        }
        const loadingFamilies = this.loadingFamiliesByDoc.get(document);
        const loadedOrLoadingFamilies = [...loadingFamilies, ...documentFamilies];
        const familiesToLoad = this.exceptFamilies(families, loadedOrLoadingFamilies);
        if (!familiesToLoad.length) {
            // When nothing new to load but some of our desired fonts are still loading,
            // probably caused by another call, lets await for those loading fonts we too
            if (loadingFamilies.length) {
                await Promise$1.all(loadingFamilies.map((family) => this.loadFont(family, document)));
            }
            return;
        }
        loadingFamilies.push(...familiesToLoad);
        await this.loadFonts(familiesToLoad, fontsData, document);
    }
    // eslint-disable-next-line class-methods-use-this
    documentFamilies(document) {
        const families = [];
        document.fonts?.forEach((font) => {
            const family = font.family.trim().replace(/"/g, '');
            if (!families.includes(family)) {
                families.push(family);
            }
        });
        return families;
    }
    // eslint-disable-next-line class-methods-use-this
    getFamilies(fonts) {
        return fonts.map((font) => font.family);
    }
    // eslint-disable-next-line class-methods-use-this
    exceptFamilies(families, except) {
        return families.filter((family) => !except.includes(family));
    }
    async loadFonts(families, fontsData, document) {
        const googleUrls = new GoogleProvider().getUrls(fontsData, families);
        const fileUrls = new FileProvider().getUrls(fontsData, families);
        const urls = [...googleUrls, ...fileUrls];
        await Promise$1.all(urls.map((url) => this.appendLink(url, document)));
        await Promise$1.all(families.map((family) => this.loadFont(family, document)));
    }
    // eslint-disable-next-line class-methods-use-this
    async appendLink(url, document) {
        await new Promise$1((resolve) => {
            const el = document.createElement('link');
            el.href = url;
            el.rel = 'stylesheet';
            el.type = 'text/css';
            el.onload = () => {
                resolve();
            };
            document.head.appendChild(el);
        });
    }
    // eslint-disable-next-line class-methods-use-this
    async loadFont(family, document) {
        // This is a "trick" to load a font: we draw a space with that font on an invisible canvas.
        // This forces the browser to load the font if it hasn't done so yet.
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const fontStyle = `12px '${family}'`;
            ctx.font = fontStyle;
            ctx.fillText('', 0, 0);
            await document.fonts.load(fontStyle);
        }
        catch (err) {
            this.container.logger.warn('loadFont()', `Error: ${err.message}`);
        }
        finally {
            const loadingFamilies = this.loadingFamiliesByDoc.get(document);
            if (loadingFamilies) {
                const foundIdx = loadingFamilies.findIndex((loadingFamily => loadingFamily === family));
                if (foundIdx >= 0) {
                    loadingFamilies.splice(foundIdx, 1);
                }
            }
        }
    }
}

export { FontsLoaderService };
